import upperFirst from 'lodash/upperFirst';
import type { Bdkv2BookProcedureDescriptor } from '@/generated/hooks/bdk/models';

/** Matches a run of the `_xNN` escapes the BDK runtime's procedure-id encoder
 *  emits for every *byte* that isn't `[A-Za-z0-9_]`. Matching the whole run
 *  rather than one escape at a time is what makes multi-byte UTF-8 decode as
 *  one character (`_xC3_xBC` → "ü", not "Ã¼"). A literal `_x` in the source
 *  name is itself escaped, as `_x5F_x78`. */
const HEX_ESCAPE_RUN_RE = /(?:_x[0-9A-Fa-f]{2})+/g;

const HEX_BYTE_RE = /[0-9A-Fa-f]{2}/g;

/** Stateless for `decode()` over a complete byte array, so one instance
 *  serves every call rather than one per escape run. */
const UTF8_DECODER = new TextDecoder();

/** A runtime-discovered procedure id, post-decode:
 *  `discover_<verb>|<service>/<entity>^(<discovery scope>)`. */
const DISCOVERED_RE = /^discover_([A-Za-z]+)\|(.+)\/([^/^]+)\^\((.*)\)$/;

/** Book signatures are authored as infinitives ("to build a file"). */
const INFINITIVE_PREFIX_RE = /^to\s+/i;

/** Markup characters that can appear in `signature.english` (`*`, `(`, `)`, `~`). */
const SIGNATURE_MARKUP_RE = /[*()~]/g;

/** SAP CDS view prefixes (`A_` API, `C_` consumption, `I_` interface,
 *  `P_` private) — technical noise in front of the business object name. */
const CDS_PREFIX_RE = /^[ACIP]_/;

/** Translation keys for the verbs a discovered procedure id can carry. */
const VERB_KEY: Record<string, string> = {
  get: 'automation.procedureVerbs.read',
  create: 'automation.procedureVerbs.create',
  update: 'automation.procedureVerbs.update',
  delete: 'automation.procedureVerbs.delete',
};

/**
 * Decodes a BDK procedure id back to the name the book actually declared.
 *
 * Statically authored procedures are plain identifiers and pass through
 * untouched; discovered ones carry `_xNN` escapes for their `|`, `/`, `^`,
 * `(` and `)` separators.
 *
 * @param procedureId - The raw procedure id from `audit.external_calls[].call`
 * @returns The decoded id
 */
export function decodeProcedureId(procedureId: string): string {
  return procedureId.replace(HEX_ESCAPE_RUN_RE, run => {
    const bytes = Uint8Array.from(run.match(HEX_BYTE_RE) ?? [], hex =>
      parseInt(hex, 16),
    );
    return UTF8_DECODER.decode(bytes);
  });
}

/** Turns a technical entity name into its business object name, keeping the
 *  natural title case: `A_PurchaseOrderItem` → "Purchase Order Item",
 *  `A_GLAccount` → "GL Account". */
function humanizeEntity(entity: string): string {
  return entity
    .replace(CDS_PREFIX_RE, '')
    .replace(/(?<=[a-z0-9])(?=[A-Z])/g, ' ')
    .replace(/(?<=[A-Z])(?=[A-Z][a-z])/g, ' ')
    .replace(/_/g, ' ')
    .trim();
}

/**
 * Formats a book procedure id as a label a business user can read.
 *
 * Runtime-discovered procedures become "<verb> <business object>" ("Read
 * Purchase Order") rather than the ~110-character encoded id the runtime uses
 * internally. Statically authored procedures are sentence-cased from their
 * snake_case name ("Extract fields from text or documents").
 *
 * Deliberately derived client-side rather than read from the book's own
 * `signature.english`: that costs a request per connection, and for discovered
 * SAP entities the authored English is currently worse (book-sap only strips
 * underscores, yielding "APurchaseOrder records").
 *
 * Only the four known verbs are translated. An unrecognized verb, and the
 * entity name itself, come from the connected system's metadata and have no
 * translation to look up.
 *
 * @param procedureId - The raw procedure id from `audit.external_calls[].call`
 * @param t - Translator for the `automation` namespace
 * @returns A human-readable label, or the decoded id when it can't be parsed
 */
export function formatProcedureLabel(
  procedureId: string,
  t: (key: string) => string,
): string {
  const decoded = decodeProcedureId(procedureId);
  if (!decoded) return '';

  const discovered = decoded.match(DISCOVERED_RE);
  if (discovered) {
    const [, verb, , entity] = discovered;
    const verbKey = VERB_KEY[verb];
    const verbLabel = verbKey ? t(verbKey) : upperFirst(verb);
    return `${verbLabel} ${humanizeEntity(entity)}`;
  }

  return upperFirst(decoded.replace(/_/g, ' '));
}

/**
 * Formats a book procedure descriptor as a title-style name, e.g.
 * `signature.english` "to build a file" → "Build a file".
 *
 * Prefers the book's authored `signature.english` (keeps articles and word
 * order the id loses), minus its markup characters and leading infinitive "to". Discovered
 * procedures and procedures without a signature fall back to
 * {@link formatProcedureLabel}, since discovered signatures are derived from
 * raw entity names and read worse than the id-based label.
 *
 * @param procedure - The procedure descriptor from the book
 * @param t - Translator for the `automation` namespace (used by the fallback)
 * @returns A human-readable name, or an empty string when there is nothing to format
 */
export function formatProcedureName(
  procedure: Pick<
    Bdkv2BookProcedureDescriptor,
    'id' | 'signature' | 'is_discovered'
  >,
  t: (key: string) => string,
): string {
  const idLabel = () => formatProcedureLabel(procedure.id ?? '', t);
  const isDiscovered =
    procedure.is_discovered ||
    DISCOVERED_RE.test(decodeProcedureId(procedure.id ?? ''));
  if (isDiscovered) return idLabel();

  const english = procedure.signature?.english
    ?.replace(SIGNATURE_MARKUP_RE, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(INFINITIVE_PREFIX_RE, '');
  return english ? upperFirst(english) : idLabel();
}
